import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'environments/environment';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

/**
 * Shows a persistent "outstanding balance" alert when a patient who still owes the
 * hospital is opened/searched at any station. Shared across Registration, Order
 * Management, ER, OPD, Pharmacy and IPD so the warning is consistent everywhere.
 *
 * Entirely gated by the per-deployment environment flag `enableOutstandingBalanceAlert`
 * (off by default) - when off, checkAndAlert() is a no-op and no request is made.
 */
@Injectable({ providedIn: 'root' })
export class OutstandingBalanceAlertService {
    // The globally-mounted breakdown modal registers itself here (see pages.component),
    // so tapping the alert can open the itemised statements -> services popup.
    breakdownModal: { open: (patientId: number) => void };

    constructor(private http: HttpClient, private toastr: ToastrService, private router: Router) { }

    checkAndAlert(patientId: number): void {
        if (!environment.enableOutstandingBalanceAlert || !patientId) {
            return;
        }

        this.http
            .get<number>(`${environment.queryModel}PatientStatmentSearch/PatientOutstandingBalance?patientId=${patientId}`)
            .pipe(catchError(() => of(0)))
            .subscribe(balance => {
                if (!balance || balance <= 0) {
                    return;
                }

                const amount = Number(balance).toFixed(3);
                const toast = this.toastr.warning(
                    `This patient still owes the hospital <b>${amount}</b>. Click here for the breakdown.`,
                    'Outstanding Balance',
                    { timeOut: 0, extendedTimeOut: 0, tapToDismiss: false, closeButton: true, enableHtml: true }
                );

                // Tap -> itemised breakdown popup (unpaid statements, each expandable to its
                // services). Collecting happens from the Collect button inside that popup.
                toast?.onTap?.subscribe(() => {
                    if (this.breakdownModal) {
                        this.breakdownModal.open(patientId);
                    } else {
                        this.router.navigateByUrl(`/pages/patientAccounting/patientDeposit/${patientId}`);
                    }
                });
            });
    }
}
